import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import { set } from 'lodash';
import EffectTypes from 'models/enums/EffectTypes';
import { useClaimTypeProvider } from '../../../ClaimType.Hooks';
import ConditionTypes from 'models/enums/ConditionTypes';
import { parseRangeValue } from 'assets/utils/parsersAndValidation/Parsers';

type Props = {
  data: Partial<Model.IClaimTypeAutomationRule>;
  onSave(data: Model.IClaimTypeAutomationRule): void;
  onCancel(): void;
};

export default function ClaimTypeAutomationRuleEditForm(props: Props) {
  const { lang } = getLocales();
  const { claimStatuses, claimJobStatuses } = useClaimTypeProvider();

  return (
    <ModalForm<Model.IClaimTypeAutomationRule>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[props.data]}
      title={lang.automationRules}
      default={props.data || {}}
      onCancel={props.onCancel}
      onSubmit={props.onSave}
      validation={(data, errors) => {
        if (!data.effects?.length) set(errors, 'effects._objectError', lang.required);
        if (!data.conditions?.length) set(errors, 'conditions._objectError', lang.required);
      }}
      render={({ form }) => {
        const updateConditionAtIndex = (index: number, newCondition: Partial<Utils.IAutomationCondition>) => {
          const updatedConditions = [...(form.data.conditions || [])];
          updatedConditions[index] = {
            ...updatedConditions[index],
            ...newCondition,
            propertyPath: 'Claim.TotalAmountCAD',
          };
          form.update({ conditions: updatedConditions });
        };

        const updateEffectAtIndex = (index: number, newEffect: Partial<Utils.IAutomationEffect>) => {
          const updatedEffects = [...(form.data.effects || [])];
          updatedEffects[index] = { ...updatedEffects[index], ...newEffect, type: EffectTypes.Assign };
          form.update({ effects: updatedEffects });
        };

        const isRange = form.data.conditions?.[0]?.type === ConditionTypes.Range;

        return (
          <FormWrapper>
            If TotalAmount is:
            <SelectInput
              data={Object.values(ConditionTypes).map((it) => ({ id: it, title: it }))}
              value={form.data.conditions?.[0]?.type}
              error={form.errors?.conditions?._objectError}
              onChange={({ type }) => {
                const existing = form.data.conditions?.[0]?.value;

                if (type === ConditionTypes.Range) {
                  form.update({
                    conditions: [
                      {
                        type: ConditionTypes.Range,
                        propertyPath: 'Claim.TotalAmountCAD',
                        value: JSON.stringify({
                          min: parseRangeValue(existing)?.min,
                          max: parseRangeValue(existing)?.max,
                        }),
                      },
                    ],
                  });
                } else {
                  const singleValue =
                    typeof existing === 'string'
                      ? (() => {
                          try {
                            const parsed = JSON.parse(existing);
                            return String(parsed.min ?? '0');
                          } catch {
                            return existing || '0';
                          }
                        })()
                      : String(existing) || '0';

                  form.update({
                    conditions: [
                      {
                        type,
                        propertyPath: 'Claim.TotalAmountCAD',
                        value: singleValue,
                      },
                    ],
                  });
                }
              }}
              name="type"
            />
            {!isRange ? (
              <>
                to:
                <NumberInput
                  name="value"
                  onChange={({ value }) => updateConditionAtIndex(0, { value: value.toString() })}
                  value={Number(form.data.conditions?.[0]?.value)}
                  step={0.01}
                  error={form.errors?.conditions?._objectError}
                />
              </>
            ) : (
              <>
                between
                <NumberInput
                  name="minValue"
                  onChange={({ minValue }) => {
                    const existing = parseRangeValue(form.data.conditions?.[0]?.value);
                    updateConditionAtIndex(0, {
                      value: JSON.stringify({ min: minValue, max: existing?.max ?? 0 }),
                    });
                  }}
                  value={parseRangeValue(form.data.conditions?.[0]?.value)?.min ?? 0}
                  step={0.01}
                  error={form.errors?.conditions?._objectError}
                />
                and
                <NumberInput
                  name="maxValue"
                  onChange={({ maxValue }) => {
                    const existing = parseRangeValue(form.data.conditions?.[0]?.value);
                    updateConditionAtIndex(0, {
                      value: JSON.stringify({ min: existing?.min ?? 0, max: maxValue }),
                    });
                  }}
                  value={parseRangeValue(form.data.conditions?.[0]?.value)?.max ?? 0}
                  step={0.01}
                  error={form.errors?.conditions?._objectError}
                />
              </>
            )}
            set ClaimStatus to:{' '}
            <SelectInput
              name="claimStatus"
              data={claimStatuses}
              onChange={({ claimStatus }) => {
                const idx = form.data.effects?.findIndex((e) => e.propertyPath === 'Claim.StatusCode');
                updateEffectAtIndex(idx, { propertyPath: 'Claim.StatusCode', value: claimStatus });
              }}
              value={form.data.effects?.find((e) => e.propertyPath === 'Claim.StatusCode')?.value}
              error={form.errors?.effects?._objectError}
            />
            and set ClaimJobStatus to:{' '}
            <SelectInput
              name="claimJobStatus"
              data={claimJobStatuses}
              onChange={({ claimJobStatus }) => {
                const idx = form.data.effects?.findIndex((e) => e.propertyPath === 'Claim.ClaimJobs[].StatusCode');
                updateEffectAtIndex(idx, { propertyPath: 'Claim.ClaimJobs[].StatusCode', value: claimJobStatus });
              }}
              value={form.data.effects?.find((e) => e.propertyPath === 'Claim.ClaimJobs[].StatusCode')?.value}
              error={form.errors?.effects?._objectError}
            />
          </FormWrapper>
        );
      }}
    />
  );
}
